import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api_url, DEFAULT_LOGO_SRC } from "../../constant/constant";

const Term = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const terms = location.state?.terms;

  const { t, i18n } = useTranslation();
  const [loadingCircle, setCircleLoading] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const token = localStorage.getItem("userToken");

  const formatTermsContent = (content) => {
    if (!content) return "";

    return content
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br/>");
  };

  const readJson = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      throw new Error("API returned HTML instead of JSON. Check the API route URL.");
    }

    return response.json();
  };

  const saveSession = (data) => {
    if (data?.token) {
      localStorage.setItem("userToken", data.token);
    }

    if (data?.user) {
      localStorage.setItem("userData", JSON.stringify(data.user));
    }

    if (data?.session_lifetime) {
      localStorage.setItem("session_lifetime", data.session_lifetime);
    }

    if (data?.language_code) {
      localStorage.setItem("lng", data.language_code);
      i18n.changeLanguage(data.language_code);
    }
  };

  const handleRequiresAction = (data) => {
    if (data?.token) {
      localStorage.setItem("userToken", data.token);
    }

    if (data?.language_code) {
      localStorage.setItem("lng", data.language_code);
      i18n.changeLanguage(data.language_code);
    }

    if (data?.status !== "requires_action") {
      return false;
    }

    if (data.code === "requires_tac_acceptance") {
      setSuccessMessage(data.message || t("termsAcceptedSuccessfully"));
      return true;
    }

    if (data.code === "requires_gender_selection") {
      const userId = data?.user?.id ?? data?.user?.user_id;
      if (userId) {
        localStorage.setItem("user_id", userId);
      }

      window.location = "/Updategender";
      return true;
    }

    if (data.code === "requires_password_change") {
      if (data?.user) {
        localStorage.setItem("userData", JSON.stringify(data.user));
      }

      window.location = "/updatepassword";
      return true;
    }

    return false;
  };

  const continueLoginWithTemporaryToken = async () => {
    const tempToken = localStorage.getItem("userToken");

    if (!tempToken) {
      throw new Error("Missing login token. Please login again.");
    }

    const response = await fetch(`${api_url}/login`, {
      mode: "cors",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: tempToken,
      }),
    });

    const data = await readJson(response);

    if (!response.ok) {
      const handled = handleRequiresAction(data);
      if (!handled) {
        throw new Error(data?.message || t("somethingWentWrong"));
      }
      return;
    }

    saveSession(data);

    if (data?.user?.force_reset_password === 1 || data?.user?.force_reset_password === true) {
      window.location = "/updatepassword";
      return;
    }

    if (data?.user?.role_id === 5) {
      if (data?.assessment_required === true) {
        localStorage.setItem("questions", JSON.stringify(data.questions || []));
        window.location = "/UserAssessment";
      } else {
        window.location = "/user/dashboard";
      }
      return;
    }

    window.location = "/dashboard";
  };

  const handleAccept = async () => {
    if (!isAccepted) return;

    if (!token) {
      setErrorMessage("Missing login token. Please login again.");
      return;
    }

    setCircleLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(`${api_url}/global/accept-terms`, {
        mode: "cors",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accepted: true,
        }),
      });

      const result = await readJson(response);

      if (!response.ok) {
        throw new Error(result.message || t("somethingWentWrong"));
      }

      if (result.status === "success") {
        setSuccessMessage(t("termsAcceptedSuccessfully"));
        await continueLoginWithTemporaryToken();
      }
    } catch (error) {
      console.error("API Error:", error);
      setErrorMessage(error.message || t("somethingWentWrong"));
    } finally {
      setCircleLoading(false);
    }
  };

  return (
    <div className="container text-center my-5 d-flex align-items-center justify-content-center flex-column">
      <div className="loginLogo mt-5">
        <img src={DEFAULT_LOGO_SRC} alt="Cyber Sense" style={{ width: "160px" }} />
      </div>

      <h2 className="m-5 txtLoginAccount">{t("Loginaccount")}</h2>

      <div className="card mt-5 pb-5 col-md-6" style={{ borderRadius: "30px" }}>
        <div className="card-body">
          {terms?.title ? (
            <h2 className="m-5 txtLoginAccount">{terms.title}</h2>
          ) : (
            <h2 className="m-5 txtLoginAccount">{t("TermsandConditions")}</h2>
          )}

          <div
            style={{
              border: "2px solid #6897CB",
              padding: "15px",
              borderRadius: "10px",
              maxHeight: "300px",
              overflowY: "auto",
              textAlign: "left",
            }}
          >
            {terms ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: formatTermsContent(terms.content || terms.content_en),
                }}
              />
            ) : (
              <p>{t("Notermsavailable")}</p>
            )}
          </div>

          {loadingCircle && (
            <div className="d-flex justify-content-center align-items-center mt-5">
              <div
                className="spinner-border loader-api"
                role="status"
                style={{
                  borderTopColor: "#6897CB",
                  borderBottomColor: "#6897CB",
                  borderLeftColor: "#6897CB",
                  marginTop: "50px",
                }}
              ></div>
            </div>
          )}

          {errorMessage && <div className="alert alert-danger mt-3">{errorMessage}</div>}
          {successMessage && <div className="alert alert-success mt-3">{successMessage}</div>}

          <div
            className="form-check mt-4"
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "30px",
            }}
          >
            <input
              className="form-check-input"
              type="checkbox"
              id="acceptTerms"
              checked={isAccepted}
              onChange={(e) => setIsAccepted(e.target.checked)}
              style={{
                height: "18px",
                width: "18px",
                border: "1px solid gray",
                marginTop: "2px",
              }}
            />

            <label className="form-check-label" htmlFor="acceptTerms">
              {t("IaccepttheTermsandConditions")}.
            </label>
          </div>

          <button
            className="btn btn-primary mt-3"
            disabled={!isAccepted || loadingCircle}
            onClick={handleAccept}
            style={{ width: "200px" }}
          >
            {loadingCircle ? t("Processing...") : t("Accept")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Term;